import { Button, Flex, FormGroup, Paragraph } from "@eworkit/iris";
import { Formik, FormikHelpers, FormikProps } from "formik";
import React, { FC, memo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMedia } from "react-use";
import * as Yup from "yup";

import { FileUploadStateItem } from "@/components/Files/upload/fileUploadReducer";
import { FormikSingleFileUpload } from "@/components/Files/upload/SingleFileUpload";
import { FormikError, VALIDATION_TYPES } from "@/components/Formik";
import { CVS_EXTENSIONS } from "@/consts/fileExtensions";
import { MEDIA_SIZE_SM } from "@/core/media";
import { Attachment, TAttachmentPurpose } from "@/data/attachments";
import { FormikForm } from "@/formikComponents";
import { Messenger } from "@/views/components/Messenger";

export interface IPrivatePersonCvFormValues {
  cv?: FileUploadStateItem<Attachment>;
}

const FORM_INITIAL_VALUES: IPrivatePersonCvFormValues = {};

const formValidationSchema = Yup.object().shape({
  cv: Yup.object()
    .test({
      message: VALIDATION_TYPES.UPLOAD_PROGRESS,
      test: (value: FileUploadStateItem<Attachment>) => !value || value.progress === 100,
    })
    .test({
      message: VALIDATION_TYPES.UPLOAD_ERRORS,
      test: (value: FileUploadStateItem<Attachment>) => !value?.error,
    })
    .test({
      message: VALIDATION_TYPES.REQUIRED,
      test: (value: FileUploadStateItem<Attachment>) => Boolean(value),
    }),
});

export const FORM_MESSENGER_CONTEXT = "onboarding-private-person-cv-form";

interface IPrivatePersonCvFormProps {
  form: FormikProps<IPrivatePersonCvFormValues>;
  onCancel: () => void;
  onSkipQuickSetup?: () => void;
  onSkipCV?: () => void;
  hideBackButton?: boolean;
}

const PrivatePersonCvForm: FC<IPrivatePersonCvFormProps> = props => {
  const isSmallScreen = useMedia(MEDIA_SIZE_SM);
  const { t } = useTranslation("session");
  const [isSubmitting] = useState(false);

  return (
    <FormikForm>
      <Flex flexDirection="column" gap="md">
        <Messenger context={FORM_MESSENGER_CONTEXT} />

        <FormGroup error={<FormikError name="cv" />}>
          <FormikSingleFileUpload
            name="cv"
            attachment={props.form.values.cv?.attachment}
            attachmentType={TAttachmentPurpose.CV}
            extensions={CVS_EXTENSIONS}
            maxSize={6}
            parsing
          />
        </FormGroup>

        <Paragraph color="n700">{t("ONBOARDING.PROFILE_TYPE_SELECT.PRIVATE_PERSON.UPLOAD_CV.HINT")}</Paragraph>

        <Flex
          flexGrow={1}
          justifyContent="space-between"
          gap="md"
          flexDirection={isSmallScreen ? "column-reverse" : "row"}>
          {!props.hideBackButton && (
            <Button variant="secondary" onClick={props.onCancel} size="lg">
              {t("ONBOARDING.PROFILE_TYPE_SELECT.PRIVATE_PERSON.UPLOAD_CV.BUTTON_CANCEL")}
            </Button>
          )}

          <Flex flexDirection={isSmallScreen ? "column-reverse" : "row"} gap="sm">
            {props.onSkipCV && (
              <Button variant="secondary" disabled={isSubmitting} onClick={props.onSkipCV} size="lg">
                {t("ONBOARDING.PROFILE_TYPE_SELECT.PRIVATE_PERSON.UPLOAD_CV.BUTTON_SKIP_CV")}
              </Button>
            )}
            <Button type="submit" disabled={props.form.isSubmitting} size="lg">
              {t("ONBOARDING.PROFILE_TYPE_SELECT.PRIVATE_PERSON.UPLOAD_CV.BUTTON_SUBMIT")}
            </Button>
          </Flex>
        </Flex>
      </Flex>
    </FormikForm>
  );
};

interface IPrivatePersonCvFormikProps extends IPrivatePersonCvFormProps {
  initialValues?: IPrivatePersonCvFormValues;
  onSubmit: (values: IPrivatePersonCvFormValues, actions: FormikHelpers<IPrivatePersonCvFormValues>) => void;
  onCancel: () => void;
}

export const PrivatePersonCvFormik: FC<Omit<IPrivatePersonCvFormikProps, "form">> = memo(
  ({ initialValues = FORM_INITIAL_VALUES, onSubmit, ...rest }) => (
    <Formik initialValues={initialValues} validationSchema={formValidationSchema} onSubmit={onSubmit}>
      {form => <PrivatePersonCvForm form={form} {...rest} />}
    </Formik>
  ),
);
